import { useMemo } from "react";
import {
  type InfiniteData,
  useInfiniteQuery,
  type QueryClient,
} from "@tanstack/react-query";

import { type FeedPost } from "@/features/stash/feedPost";
import { stashListQueryKey } from "@/features/stash/stashQueryKeys";
import { resolveSaveDiscoveryInterval } from "@/features/stash/saveActivity";
import { useStashPosts } from "@/hooks/useStashPosts";
import {
  postsService,
  type StashIdListRequest,
} from "@/services/posts.service";

export {
  extractMediaAttachments,
  mapPostToFeed,
} from "@/features/stash/feedPost";
export type {
  FeedPost,
  MediaAttachment,
  MediaType,
} from "@/features/stash/feedPost";

export interface RecentPostsFilters {
  routingClass?: string;
  category?: string;
  source?: string;
  dateFrom?: string;
  dateTo?: string;
  sort?: "asc" | "desc";
  intent?: string;
}

export interface RecentStashPage {
  stashIds: string[];
  hasMore: boolean;
  nextCursor?: string;
}

export const DEFAULT_RECENT_POSTS_PAGE_SIZE = 25;

interface RecentStashPageParam {
  cursor: string | null;
  page: number;
}
type RecentStashesInfiniteData = InfiniteData<RecentStashPage, RecentStashPageParam>;

export const recentStashesQueryKey = (filters: RecentPostsFilters) =>
  ["stashes", "recent", "ids", JSON.stringify(filters)] as const;

export const addRecentStashId = (
  client: QueryClient,
  stashId: string,
) => {
  client.setQueryData<RecentStashesInfiniteData>(
    recentStashesQueryKey({}),
    current => {
      if (!current) return current;
      const pages = current.pages.map((page, index) => {
        const withoutStash = page.stashIds.filter(id => id !== stashId);
        if (index === 0) {
          return { ...page, stashIds: [stashId, ...withoutStash] };
        }
        return withoutStash.length === page.stashIds.length
          ? page
          : { ...page, stashIds: withoutStash };
      });
      return { ...current, pages };
    },
  );
};

export const removeRecentStashId = (
  client: QueryClient,
  stashId: string,
) => {
  client.setQueriesData<RecentStashesInfiniteData>(
    { queryKey: ["stashes", "recent", "ids"] },
    current => {
      if (!current) return current;
      const pages = current.pages.map(page => {
        const stashIds = page.stashIds.filter(id => id !== stashId);
        return stashIds.length === page.stashIds.length ? page : { ...page, stashIds };
      });
      return { ...current, pages };
    },
  );
};

const createRecentStashesQueryFn = (
  pageSize: number,
  filters: RecentPostsFilters,
) => async ({ pageParam, client }: { pageParam: RecentStashPageParam; client: QueryClient }) => {
  const request: StashIdListRequest = {
    cursor: pageParam.cursor,
    routing_class: filters.routingClass,
    category: filters.category,
    source: filters.source,
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    sort: filters.sort,
    overall_message_intent: filters.intent,
  };
  const response = await postsService.listStashIds({ ...request, limit: pageSize });
  const stashIds = Array.from(new Set(response.stash_ids));
  const nextCursor = response.next_cursor?.trim() || undefined;

  // New ids load themselves through useStashPosts. As in native's discovery, a
  // tick refreshes the cards likely on screen, not every card loaded below them.
  if (pageParam.page === 0) {
    for (const id of stashIds) {
      void client.invalidateQueries({
        queryKey: stashListQueryKey(id),
        exact: true,
        refetchType: "active",
      });
    }
  }

  return {
    stashIds,
    hasMore: nextCursor !== undefined,
    nextCursor,
  } satisfies RecentStashPage;
};

export const createRecentPostsInfiniteOptions = (
  pageSize: number,
  filters: RecentPostsFilters,
) => {
  const queryKey = recentStashesQueryKey(filters);
  return {
    queryKey,
    initialPageParam: { cursor: null, page: 0 } satisfies RecentStashPageParam,
    staleTime: 0,
    refetchOnMount: "always" as const,
    // Coming back to the app is a visit: ask for new saves.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // While the app stays open, ask on a fixed cadence; in-flight saves are
    // followed per stash, not by re-reading the list.
    refetchInterval: resolveSaveDiscoveryInterval(),
    queryFn: createRecentStashesQueryFn(pageSize, filters),
    getNextPageParam: (
      lastPage: RecentStashPage,
      _allPages: RecentStashPage[],
      _lastPageParam: RecentStashPageParam,
      allPageParams: RecentStashPageParam[],
    ) => {
      if (!lastPage.hasMore || !lastPage.nextCursor) return undefined;
      if (allPageParams.some(page => page.cursor === lastPage.nextCursor)) return undefined;
      return {
        cursor: lastPage.nextCursor,
        page: allPageParams.length,
      } satisfies RecentStashPageParam;
    },
  };
};

const flattenRecentStashIds = (data: RecentStashesInfiniteData | undefined) => {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const page of data?.pages ?? []) {
    for (const id of page.stashIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
};

export const useRecentPostsInfiniteQuery = (
  pageSize = DEFAULT_RECENT_POSTS_PAGE_SIZE,
  filters: RecentPostsFilters = {},
  enabled = true,
) => {
  const query = useInfiniteQuery({
    ...createRecentPostsInfiniteOptions(pageSize, filters),
    enabled,
  });
  const data = query.data as RecentStashesInfiniteData | undefined;
  const stashIds = useMemo(
    () => (enabled ? flattenRecentStashIds(data) : []),
    [data, enabled],
  );
  const hydrated = useStashPosts(stashIds);
  const posts = useMemo(
    () => hydrated.posts.filter(post => post.user_state !== "removed" && post.user_state !== "none"),
    [hydrated.posts],
  );

  return {
    ...query,
    data,
    stashIds,
    posts,
    isLoading: query.isLoading || (Boolean(query.data) && hydrated.isLoading),
  };
};

export const useRecentStashes = (limit = DEFAULT_RECENT_POSTS_PAGE_SIZE) => {
  const query = useRecentPostsInfiniteQuery(limit);
  const firstPage = query.data?.pages[0];
  const postsById = useMemo(
    () => new Map(query.posts.map(post => [post.id, post])),
    [query.posts],
  );
  const firstPagePosts = useMemo(
    () => firstPage?.stashIds.map(id => postsById.get(id)).filter((post): post is FeedPost => Boolean(post)),
    [firstPage?.stashIds, postsById],
  );

  return {
    data: firstPage ? firstPagePosts : undefined,
    total: firstPage && !firstPage.hasMore ? firstPagePosts?.length : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    pageCount: query.data?.pages.length ?? 0,
  };
};

export const prefetchRecentPosts = (
  queryClient: QueryClient,
  pageSize: number = DEFAULT_RECENT_POSTS_PAGE_SIZE,
  filters: RecentPostsFilters = {},
) => queryClient.prefetchInfiniteQuery(createRecentPostsInfiniteOptions(pageSize, filters));

export const useInfiniteRecentPosts = (
  pageSize = DEFAULT_RECENT_POSTS_PAGE_SIZE,
  filters: RecentPostsFilters = {},
) => useRecentPostsInfiniteQuery(pageSize, filters, true);
